/**
 * Overview screen — hero KPIs across every feature area + reconciliation
 * strip + snapshot balances. One round-trip per data source, all fired in
 * parallel; the strip surfaces the ledger status even before the KPI grid
 * paints.
 *
 * Data sources
 *   - GET /admin/metrics/overview     (Phase C rollup — takes start/end)
 *   - GET /admin/metrics/financial    (balances + wallet liability)
 *   - GET /admin/metrics/reconciliation
 */

import { api, ApiError } from '../../api.js';
import { toast } from '../../components/toast.js';
import {
  compositionRows,
  emptyState,
  errorState,
  escape,
  fetchOrNull,
  fmtBps,
  fmtInt,
  fmtKobo,
  fromToQuery,
  kpi,
  mountPieChart,
  rangeQuery,
  skeletonGrid,
  snapshotKpi,
} from './common.js';

export async function renderOverview(host, ctx) {
  host.innerHTML = skeletonGrid(8);
  try {
    // Earnings endpoint gives us the per-source revenue breakdown we
    // need for the pie chart (rake + eraser rev + 50/50 rev + live
    // entry rev). Fire in parallel with the rest — fetchOrNull so the
    // page still paints if the earnings call errors.
    const [ov, fin, recon, earnings] = await Promise.all([
      api(`/admin/metrics/overview${rangeQuery(ctx.range, { granularity: false })}`),
      fetchOrNull(`/admin/metrics/financial${fromToQuery(ctx.range)}`),
      fetchOrNull('/admin/metrics/reconciliation'),
      fetchOrNull(`/admin/metrics/earnings${rangeQuery(ctx.range)}`),
    ]);
    host.innerHTML = _render({ ov, fin, recon, ctx });
    _wire(host, ctx, { earnings });
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      ctx.navigate('/login');
      return;
    }
    toast.fromError(err);
    host.innerHTML = errorState(err);
  }
}

function _render({ ov, fin, recon, ctx }) {
  const balanced = recon ? recon.ok : true;
  const strip = _reconStrip(recon);
  const heroCards = [
    kpi({
      label: 'Net revenue',
      value: fmtKobo(ov?.earnings?.net_revenue_kobo),
      hint: 'rake + power-ups + live entry − fees',
      hero: true,
    }),
    kpi({
      label: 'GGR / rake',
      value: fmtKobo(ov?.earnings?.ggr_rake_kobo),
      hint: 'house cut from duels',
      hero: true,
    }),
    kpi({
      label: 'Take rate',
      value: fmtBps(ov?.earnings?.take_rate_bps),
      hint: 'rake ÷ handle',
      hero: true,
    }),
    kpi({
      label: 'ARPPU',
      value: fmtKobo(ov?.earnings?.arppu_kobo),
      hint: 'rev ÷ paying player',
      hero: true,
    }),
  ].join('');

  const windowedCards = [
    kpi({
      label: 'Paying-player %',
      value: fmtBps(ov?.earnings?.paying_player_pct_bps),
      hint: 'paying ÷ active',
    }),
    kpi({
      label: 'Duels created',
      value: fmtInt(ov?.duels?.created),
      hint: 'this period',
    }),
    kpi({
      label: 'Duels completed',
      value: fmtInt(ov?.duels?.completed),
      hint: 'this period',
    }),
    kpi({
      label: 'Duel rake',
      value: fmtKobo(ov?.duels?.rake_kobo),
      hint: 'this period',
    }),
    kpi({
      label: 'Erasers sold',
      value: fmtInt(ov?.powerups?.eraser_units_sold),
      hint: 'units',
    }),
    kpi({
      label: 'Power-up revenue',
      value: fmtKobo(ov?.powerups?.powerup_revenue_kobo),
      hint: 'erasers + 50/50',
    }),
    kpi({
      label: 'Live entry revenue',
      value: fmtKobo(ov?.earnings?.live_entry_revenue_kobo),
      hint: 'paid joins − refunds',
    }),
    kpi({
      label: '50/50 used',
      value: fmtInt(ov?.powerups?.fiftyfifty_used_total),
      hint: 'activations',
    }),
    kpi({
      label: 'Referrals rewarded',
      value: fmtInt(ov?.friends?.referrals_rewarded),
      hint: 'this period',
    }),
    kpi({
      label: 'Friendships accepted',
      value: fmtInt(ov?.friends?.friendships_accepted),
      hint: 'this period',
    }),
    kpi({
      label: 'Payment fees',
      value: fmtKobo(ov?.earnings?.payment_fees_kobo),
      hint: 'processor cost',
    }),
  ].join('');

  const snapshotCards = [
    snapshotKpi({
      label: 'Outstanding eraser liability',
      value: fmtInt(ov?.powerups?.outstanding_eraser_balance),
      hint: 'unspent erasers owed',
    }),
    snapshotKpi({
      label: 'Outstanding 50/50 liability',
      value: fmtInt(ov?.powerups?.outstanding_fiftyfifty_balance),
      hint: 'unspent 50/50 owed',
    }),
    snapshotKpi({
      label: 'Platform revenue balance',
      value: fmtKobo(fin?.platform_revenue_kobo),
      hint: 'available · house',
    }),
    snapshotKpi({
      label: 'Wallet liability (owed)',
      value: fmtKobo(fin?.wallet_liability_kobo),
      hint: 'user balances',
    }),
  ].join('');

  const compRows = fin
    ? compositionRows([
        {
          label: 'Deposits (windowed)',
          value: fin.deposits?.totals?.succeeded_kobo || 0,
          display: fmtKobo(fin.deposits?.totals?.succeeded_kobo),
          pct: 100,
          color: '#1E3A8A',
        },
        {
          label: 'Withdrawals (windowed)',
          value: fin.withdrawals?.totals?.succeeded_kobo || 0,
          display: fmtKobo(fin.withdrawals?.totals?.succeeded_kobo),
          pct: Math.min(
            100,
            (100 * (fin.withdrawals?.totals?.succeeded_kobo || 0)) /
              Math.max(1, fin.deposits?.totals?.succeeded_kobo || 0),
          ),
          color: '#E08177',
        },
        {
          label: 'Net flow',
          value: fin.net_flow_kobo || 0,
          display: fmtKobo(fin.net_flow_kobo),
          pct: 60,
          color: '#0E7C5A',
        },
      ])
    : '<div class="adm2-kpi-hint">Loading composition…</div>';

  return `
    ${strip}
    <div class="adm2-section-label">Hero metrics · this period</div>
    <div class="adm2-grid hero">${heroCards}</div>

    <div class="adm2-section-label">Activity · windowed to date range</div>
    <div class="adm2-grid">${windowedCards}</div>

    <div style="display:flex;align-items:center;gap:10px;margin:26px 0 12px;">
      <span style="font:600 12px 'IBM Plex Sans',sans-serif;letter-spacing:.04em;text-transform:uppercase;color:var(--a-warm-ink-2);">Live snapshots</span>
      <span style="font:400 11px 'IBM Plex Sans',sans-serif;color:var(--a-warm-ink-2);">— current balances, not affected by the date range</span>
      <span style="flex:1;height:1px;background:var(--a-warm-bd);"></span>
    </div>
    <div class="adm2-grid">${snapshotCards}</div>

    <div class="adm2-panel" style="margin-top:26px;">
      <div class="adm2-panel-title">Cash flow this window</div>
      <div class="adm2-panel-sub">windowed to your date range · powered by /admin/metrics/financial</div>
      <div style="margin-top:14px;">${compRows}</div>
    </div>

    <div class="adm2-panel" style="margin-top:16px;">
      <div class="adm2-panel-title">Platform earnings by source</div>
      <div class="adm2-panel-sub">windowed to your date range · powered by /admin/metrics/earnings</div>
      <div id="adm2-overview-earnings-pie" style="margin-top:14px;"></div>
    </div>
  `;
}

function _reconStrip(recon) {
  if (!recon) {
    return `<div class="adm2-recon ok"><div class="adm2-recon-dot"></div>
      <div><div class="adm2-recon-title">Ledger status</div>
      <div class="adm2-recon-sub">reconciliation not yet loaded</div></div></div>`;
  }
  const balanced = !!recon.ok;
  const cls = balanced ? 'ok' : 'bad';
  const title = balanced ? 'Ledger reconciled' : 'Ledger out of balance';
  const sub = balanced
    ? `debits = credits · global diff ${fmtKobo(0)} · ${fmtInt(recon.unbalanced_txns_count)} unbalanced txns`
    : `global diff ${fmtKobo(recon.global_diff_kobo)} · ${fmtInt(recon.unbalanced_txns_count)} unbalanced txns`;
  return `
    <div class="adm2-recon ${cls}">
      <div class="adm2-recon-dot"></div>
      <div style="flex:1;min-width:220px;">
        <div class="adm2-recon-title">${escape(title)}</div>
        <div class="adm2-recon-sub">${escape(sub)}</div>
      </div>
      <a href="/admin/v2/financial" data-link class="adm2-btn primary" style="text-decoration:none;">View detail →</a>
    </div>
  `;
}

function _wire(host, ctx, { earnings } = {}) {
  void ctx; // links use data-link + the router handles them.
  const pieHost = host.querySelector('#adm2-overview-earnings-pie');
  if (pieHost) {
    const totals = (earnings && earnings.totals) || {};
    // Order the sources by rough revenue magnitude so the legend reads
    // top-down like an income statement. Colours are the same tokens
    // the rest of admin_v2 uses so returning users can pattern-match:
    // teal = duel-adjacent, gold = eraser, blue = 50/50, plum = live.
    mountPieChart(pieHost, {
      slices: [
        {
          label: '1-v-1 duel rake',
          value: Number(totals.duel_rake_kobo || 0),
          color: '#23C9A0',
        },
        {
          label: 'Eraser sales',
          value: Number(totals.eraser_revenue_kobo || 0),
          color: '#E5C100',
        },
        {
          label: '50/50 sales',
          value: Number(totals.fiftyfifty_revenue_kobo || 0),
          color: '#7FB4FF',
        },
        {
          label: 'Live trivia entry fees',
          value: Number(totals.live_entry_revenue_kobo || 0),
          color: '#B18CFE',
        },
      ],
      valueFmt: (v) => fmtKobo(v),
      emptyMessage: earnings === null
        ? 'Earnings feed unavailable — try refreshing.'
        : 'No platform earnings in this window yet.',
    });
  }
}
